import { Group, Mesh } from 'three';
import { BOAT } from '../core/config';
import type { Pose } from '../controls/types';
import { LEVEL_RIDE, type BoatRide } from '../controls/boat';
import type { PaletteModel } from '../data/types';
import type { MaterialFactory } from '../render/types';
import { EDGE_ID } from '../world/mesh/attributes';
import { createMeshBuilder } from '../world/mesh/meshBuilder';
import { buildBoatBody, buildHoldMask } from '../world/harbour/openBoat';
import { hullStylesOf } from '../world/harbour/hullParts';
import { floatingHeight } from '../world/harbour/shipMeshes';
import { SHIP_TEMPLATES } from '../world/harbour/shipTemplates';

export interface TourBoat {
  readonly root: Group;
  /** `ride` lays the hull on moving water; without it the boat lies level on still water. */
  update(pose: Pose, bearing: number, visible: boolean, ride?: BoatRide): void;
  dispose(): void;
}

/* The water is drawn after the hull and after the mask over its hold, so it fails the depth test
   wherever a view looks into the boat. */
/* The boat floats as the fleet's rowing boats do. At the bare water level its gunwale lay in the
   water amidships and the boat looked swamped. */
const FLOATING_Y = floatingHeight({ type: 'rowing' });
const HULL_ORDER = -2;
const MASK_ORDER = -1;

/** The rowing boat follows the route bearing while the camera can look around from its aft bench. */
export function createTourBoat(palette: PaletteModel, materials: MaterialFactory): TourBoat {
  const colours = {
    hull: palette.shipHull.median.linear,
    wale: palette.shipHull.p90.linear,
    rail: palette.highlightWhite.median.linear,
    port: palette.shipHull.p10.linear,
    rope: palette.lineworkDark.median.linear,
  };
  const builder = createMeshBuilder(3072);
  buildBoatBody(builder, SHIP_TEMPLATES.rowing, 'near', colours, EDGE_ID.max, 0);
  const geometry = builder.finish();
  const mesh = new Mesh(geometry, materials.painted());
  mesh.name = 'tour-rowing-boat';
  mesh.renderOrder = HULL_ORDER;
  const maskBuilder = createMeshBuilder(64);
  buildHoldMask(maskBuilder, SHIP_TEMPLATES.rowing, hullStylesOf(colours, EDGE_ID.max, 0).hull);
  const maskGeometry = maskBuilder.finish();
  const mask = new Mesh(maskGeometry, materials.painted({ depthOnly: true }));
  mask.name = 'tour-rowing-boat-hold-mask';
  mask.renderOrder = MASK_ORDER;
  const root = new Group();
  // Turned to its bearing first, then rolled and pitched about its own axes.
  root.rotation.order = 'YXZ';
  root.name = 'tour-boat';
  root.visible = false;
  root.add(mesh, mask);

  return {
    root,
    update(pose, bearing, visible, ride = LEVEL_RIDE): void {
      root.visible = visible;
      if (!visible) return;
      // The hull's bow is -X; camera yaw zero looks towards -Z.
      root.rotation.set(ride.roll, bearing - Math.PI / 2, ride.pitch);
      root.position.set(
        pose.position[0] - Math.sin(bearing) * BOAT.benchAftM,
        FLOATING_Y + ride.heave,
        pose.position[2] - Math.cos(bearing) * BOAT.benchAftM
      );
    },
    dispose(): void {
      root.removeFromParent();
      root.clear();
      geometry.dispose();
      maskGeometry.dispose();
    },
  };
}
